import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ReloadIcon } from '@radix-ui/react-icons';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { useHistory } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { confirmResetPassword } from 'aws-amplify/auth';

const schema = yup
  .object({
    code: yup.string().required('Verification code is required'),
    password: yup
      .string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required.'),
  })
  .required();

const AdminVerificationCode = ({ UserEmail }) => {
  const {
    register,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const history = useHistory();

  async function handleConfirmResetPassword({ code, password }) {
    try {
      await confirmResetPassword({
        username: UserEmail,
        confirmationCode: code,
        newPassword: password,
      });
      
      // Redirect to admin login after successful password reset
      history.push('/admin/login');
    } catch (err) {
      setError('root', {
        message: err.message || 'Failed to reset password. Please try again.',
      });
      console.log('Admin password reset confirmation error:', err);
    }
  }

  const onSubmit = async (data) => {
    await handleConfirmResetPassword(data);
  };

  return (
    <div className='relative min-h-screen bg-zinc-900'>
      <div
        className='fixed w-full h-full z-0 bg-cover bg-repeat bg-center'
        style={{ backgroundImage: `url(/bg-new.jpg)` }}
      />

      <div className='flex flex-col h-full items-center justify-center relative z-10'>
        <div className='mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12'>
          <img
            alt='logo'
            src='/TriVista_Horizontal_Logo_White.png'
            className='w-[300px]'
          />
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='max-w-2xl flex flex-col items-center justify-center mx-8'>
            <h1 className='text-3xl text-white text-center lg:tracking-[0.75rem] tracking-[0.5rem]'>
              Admin Portal
            </h1>
            <div className='mt-6 text-white text-lg text-center'>
              An email with a verification code has been sent to{' '}
              <b>{UserEmail}</b>. Enter the code and your new password below:
            </div>
            
            <Card className='mt-4 w-full mx-8 md:mx-auto md:w-auto px-4'>
              {errors.root && (
                <Alert variant='destructive' className='mt-4 max-w-96'>
                  <ExclamationTriangleIcon className='h-4 w-4' />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{errors.root.message}</AlertDescription>
                </Alert>
              )}
              
              <div className='my-4'>
                <Label className='text-xl'>Verification Code</Label>
                <Input
                  className='h-8 w-80 sm:w-auto sm:min-w-80'
                  {...register('code')}
                />
                <div>
                  {errors.code && (
                    <p className='text-red-500 mt-1'>{errors.code.message}</p>
                  )}
                </div>
              </div>
              
              <div className='mt-4'>
                <Label className='text-xl'>New Password</Label>
                <Input
                  className='h-8 w-80 sm:w-auto sm:min-w-80'
                  type='password'
                  {...register('password')}
                />
                <div className='mt-1 flex gap-3 items-right'>
                  {errors.password && (
                    <p className='text-red-500'>{errors.password.message}</p>
                  )}
                </div>
              </div>
              
              <Button
                className='my-4 w-full px-12 py-6 text-xl rounded-xl'
                type='submit'
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <ReloadIcon className='w-6 h-6 animate-spin mr-2' />
                )}
                {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </Card>
          </div>
        </form>
        
        <Link to={'/admin/login'} className='text-white mt-4 flex items-center gap-2'>
          <ArrowLeft className='h-3 w-3' /> Back to Admin Login
        </Link>
      </div>
    </div>
  );
};

export default AdminVerificationCode;